import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ServerEvent, WireMessage, WireRun } from '@converoom/contracts';
import { parseMentions } from '@converoom/contracts';
import { api, ApiError, newClientId } from '../lib/api';
import type { Realtime, ConnState } from '../lib/realtime';
import type { Me, Project, Room, Connection } from '../lib/types';
import { MessageItem, PendingRun } from '../components/Message';
import { Composer } from '../components/Composer';
import { PROVIDER_NAME } from '../lib/format';

interface Props {
  me: Me;
  project: Project;
  room: Room;
  rt: Realtime;
  conn: ConnState;
  connections: Connection[];
  drawerOpen: boolean;
  onOpenMenu: () => void;
  onToggleDrawer: () => void;
}

export function RoomView({
  me,
  project,
  room,
  rt,
  conn,
  connections,
  drawerOpen,
  onOpenMenu,
  onToggleDrawer,
}: Props) {
  const [messages, setMessages] = useState<WireMessage[]>([]);
  const [runs, setRuns] = useState<Record<string, WireRun & { partial: string }>>({});
  const [typing, setTyping] = useState<Record<string, { name: string; until: number }>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [replyTo, setReplyTo] = useState<WireMessage | null>(null);
  // "Devam" mode: the AI(s) this user last called in this room keep answering follow-ups until turned off.
  const followKey = `cr.followup.${room.id}`;
  const [followUp, setFollowUpState] = useState<string[]>([]);
  useEffect(() => {
    try {
      setFollowUpState(JSON.parse(localStorage.getItem(followKey) ?? '[]'));
    } catch {
      setFollowUpState([]);
    }
  }, [followKey]);
  const setFollowUp = (v: string[]) => {
    setFollowUpState(v);
    try {
      if (v.length) localStorage.setItem(followKey, JSON.stringify(v));
      else localStorage.removeItem(followKey);
    } catch {
      /* storage unavailable: in-memory only */
    }
  };
  const streamRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);
  const latestSeq = useRef(0);

  const merge = useCallback((incoming: WireMessage[]) => {
    setMessages((prev) => {
      const byId = new Map(prev.map((m) => [m.id, m]));
      for (const m of incoming) byId.set(m.id, m);
      const out = [...byId.values()].sort((a, b) => a.seq - b.seq);
      latestSeq.current = out.at(-1)?.seq ?? latestSeq.current;
      return out;
    });
  }, []);

  // Initial load + realtime subscription.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get<WireMessage[]>(`/v1/rooms/${room.id}/messages?limit=100`)
      .then((m) => alive && merge(m))
      .catch(() => alive && setError('Mesajlar yüklenemedi.'))
      .finally(() => alive && setLoading(false));
    // Run identity/state for AI messages (cost is only returned for runs the viewer paid for).
    api
      .get<(WireRun & { createdAt: string })[]>(`/v1/rooms/${room.id}/runs`)
      .then(
        (rs) =>
          alive &&
          setRuns((prev) => {
            const next = { ...prev };
            for (const r of rs) next[r.id] = { ...r, roomId: room.id, partial: prev[r.id]?.partial ?? '' };
            return next;
          }),
      )
      .catch(() => {});
    rt.subscribeRoom(room.id);
    const off = rt.on((e: ServerEvent) => {
      if (e.t === 'message' && e.message.roomId === room.id) merge([e.message]);
      else if (e.t === 'room.subscribed' && e.roomId === room.id) {
        // (Re)subscribed — fetch anything missed while disconnected; never re-send anything.
        if (latestSeq.current && e.latestSeq > latestSeq.current)
          api
            .get<WireMessage[]>(`/v1/rooms/${room.id}/messages?afterSeq=${latestSeq.current}&limit=200`)
            .then(merge)
            .catch(() => {});
        setRuns((prev) => {
          const next = { ...prev };
          for (const r of e.activeRuns)
            next[r.id] = { ...r, partial: r.partialText ?? next[r.id]?.partial ?? '' };
          return next;
        });
      } else if (e.t === 'run.update' && e.run.roomId === room.id) {
        setRuns((prev) => ({
          ...prev,
          [e.run.id]: { ...e.run, partial: prev[e.run.id]?.partial ?? e.run.partialText ?? '' },
        }));
      } else if (e.t === 'run.delta' && e.roomId === room.id) {
        setRuns((prev) => {
          const r = prev[e.runId];
          if (!r) return prev;
          return { ...prev, [e.runId]: { ...r, partial: r.partial + e.text } };
        });
      } else if (e.t === 'typing' && e.roomId === room.id && e.userId !== me.id) {
        setTyping((t) => ({ ...t, [e.userId]: { name: e.name, until: Date.now() + 4000 } }));
      } else if (e.t === 'access.revoked' && e.roomId === room.id) {
        setError('Bu odaya erişiminiz kaldırıldı.');
        setMessages([]);
      }
    });
    const tick = setInterval(
      () => setTyping((t) => Object.fromEntries(Object.entries(t).filter(([, v]) => v.until > Date.now()))),
      1500,
    );
    return () => {
      alive = false;
      off();
      clearInterval(tick);
      rt.unsubscribeRoom(room.id);
    };
  }, [room.id, rt, merge, me.id]);

  // Auto-scroll only when the reader is already at the bottom.
  useEffect(() => {
    const el = streamRef.current;
    if (el && stickToBottom.current) el.scrollTop = el.scrollHeight;
  }, [messages, runs]);

  const activeRuns = useMemo(
    () =>
      Object.values(runs).filter((r) => ['BUDGET_RESERVED', 'RUNNING', 'CANCEL_REQUESTED'].includes(r.state)),
    [runs],
  );
  const runById = runs;

  async function send(text: string, attachmentIds: string[]) {
    const clientMsgId = newClientId();
    const explicit = parseMentions(text).map((m) => m.provider);
    const continueWith = explicit.length === 0 && room.aiEnabled ? followUp : [];
    const body = {
      text,
      clientMsgId,
      attachmentIds,
      continueWith,
      ...(replyTo ? { replyToId: replyTo.id } : {}),
    };
    // Retry once on network failure with the SAME clientMsgId → server de-duplicates (no double charge).
    try {
      const r = await api
        .post<{ message: WireMessage; runs: WireRun[] }>(`/v1/rooms/${room.id}/messages`, body)
        .catch(async (e) => {
          if (e instanceof ApiError) throw e;
          await new Promise((res) => setTimeout(res, 800));
          return api.post<{ message: WireMessage; runs: WireRun[] }>(`/v1/rooms/${room.id}/messages`, body);
        });
      merge([r.message]);
      if (explicit.length) setFollowUp(explicit);
      setRuns((prev) => {
        const next = { ...prev };
        for (const run of r.runs) if (!next[run.id]) next[run.id] = { ...run, partial: '' }; // realtime may already be newer
        return next;
      });
      setReplyTo(null);
      stickToBottom.current = true;
      return true;
    } catch (e) {
      setError(
        e instanceof ApiError
          ? `Mesaj gönderilemedi (${e.code}).`
          : 'Bağlantı yok — mesaj gönderilmedi. Tekrar deneyin.',
      );
      return false;
    }
  }

  async function cancel(runId: string) {
    await api.post(`/v1/runs/${runId}/cancel`).catch(() => {});
  }

  async function edit(m: WireMessage) {
    const text = prompt('Mesajı düzenle', m.text);
    if (text && text !== m.text) merge([await api.patch<WireMessage>(`/v1/messages/${m.id}`, { text })]);
  }

  const connectedProviders = new Set(connections.map((c) => c.provider));
  const typingNames = Object.values(typing).map((t) => t.name);

  return (
    <>
      <header className="topbar">
        <button
          className="btn icon-btn ghost only-narrow"
          onClick={onOpenMenu}
          aria-label="Projeler ve odalar menüsü"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
            <path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="1.8" />
          </svg>
        </button>
        <div style={{ minWidth: 0 }}>
          <h1>{room.name}</h1>
          <div className="room-meta">
            {project.name} — {room.kind === 'private' ? 'Özel oda: yalnızca üyeler' : 'Proje odası'}
            {!room.aiEnabled && ' — AI kapalı'}
          </div>
        </div>
        <span className="spacer" />
        {conn !== 'open' && (
          <span className="conn-state" role="status">
            {conn === 'reconnecting'
              ? 'Yeniden bağlanıyor…'
              : conn === 'closed'
                ? 'Bağlantı kapalı'
                : 'Bağlanıyor…'}
          </span>
        )}
        <button
          className="btn small"
          onClick={onToggleDrawer}
          aria-expanded={drawerOpen}
          aria-controls="project-drawer"
        >
          Panel
        </button>
      </header>

      <div
        className="stream"
        ref={streamRef}
        onScroll={(e) => {
          const el = e.currentTarget;
          stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        }}
      >
        <div
          className="stream-inner"
          role="log"
          aria-live="polite"
          aria-relevant="additions"
          aria-label="Oda mesajları"
        >
          {loading && <p className="hint">Yükleniyor…</p>}
          {!loading && messages.length === 0 && (
            <div className="empty">
              <h2>Odada henüz mesaj yok</h2>
              <p>
                Ekibinizle yazışın. Bir AI'ı çağırmak için mesajınıza <strong>@claude</strong>,{' '}
                <strong>@codex</strong>, <strong>@gemini</strong> veya ücretsiz <strong>@ai</strong> ekleyin;
                çağrı sizin bağlantınız ve bütçenizle yapılır. Etiket yoksa hiçbir AI çalışmaz.
              </p>
            </div>
          )}
          {messages.map((m) => (
            <MessageItem
              key={m.id}
              m={m}
              me={me}
              run={m.aiRunId ? runById[m.aiRunId] : undefined}
              replyTarget={m.replyToId ? messages.find((x) => x.id === m.replyToId) : undefined}
              onQuote={() => setReplyTo(m)}
              onEdit={() => edit(m)}
            />
          ))}
          {activeRuns.map((r) => (
            <PendingRun key={r.id} run={r} mine={r.actorUserId === me.id} onCancel={() => cancel(r.id)} />
          ))}
        </div>
      </div>

      <div className="typing" aria-live="polite">
        {typingNames.length > 0 && `${typingNames.join(', ')} yazıyor…`}
      </div>
      {error && (
        <div className="notice" role="alert" style={{ margin: '0 var(--s4) var(--s2)' }}>
          {error}{' '}
          <button className="btn small ghost" onClick={() => setError(null)}>
            Kapat
          </button>
        </div>
      )}
      <Composer
        roomId={room.id}
        aiEnabled={room.aiEnabled}
        replyTo={replyTo}
        onCancelReply={() => setReplyTo(null)}
        onSend={send}
        onTyping={() => rt.send({ t: 'typing', roomId: room.id })}
        mentionStatus={(text) => {
          const explicit = parseMentions(text);
          const list = explicit.length ? explicit.map((m) => m.provider) : followUp;
          return list.map((p) => ({
            provider: p,
            label: PROVIDER_NAME[p] ?? p,
            connected: connectedProviders.has(p),
          }));
        }}
        followUp={followUp.map((p) => ({ provider: p, label: PROVIDER_NAME[p] ?? p }))}
        onClearFollowUp={() => setFollowUp([])}
      />
    </>
  );
}
